import {
    classNames,
    Keyboard,
    layout,
    NameKeyboardLayoutDE,
    NameKeyboardLayoutEN,
    PushButton,
    TextInput,
    typography,
    useTypeRef,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from '@variocube/cube-ui';
import * as React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import {Language} from '@variocube/app-api';
import {stylesheet} from 'typestyle';
import {useLocalization} from "../../i18n";

enum RecipientField {
    name,
}


interface RecipientNameViewProps {
    onNext: (recipientName: string) => void;
    onCancel: () => void;
    carrierId?: string;
}

export function RecipientNameView({onNext, onCancel, carrierId}: RecipientNameViewProps) {
    const {t, language} = useLocalization();
    const [nameType, setNameType] = useTypeRef();
    const [field, setField] = useState<RecipientField>(RecipientField.name);
    const nameRef = useRef<HTMLInputElement>();
    const [name, setName] = useState<string>("");
    const [nameWasTyped, setNameWasTyped] = useState<boolean>();
    const [confirm, setConfirm] = useState<boolean>();

    const handleNameFocus = useCallback(() => setField(RecipientField.name), []);

    const handleConfirm = useCallback(() => {
        setConfirm(true);
    }, [setConfirm]);

    const handleBack = useCallback(() => {
        setConfirm(false);
    }, [setConfirm]);

    const handleNextClick = useCallback(() => {
        onNext(name);
    }, [name, onNext]);

    const handleNameChanged = useCallback((name: string) => {
        setName(name);
        setNameWasTyped(true);
    }, []);

    const nameValid = useMemo<boolean>(() => {
        switch (carrierId) {
            case "dhl":
                let parts = name.trim().split(" ");
                return parts?.length > 1 && parts[0].length > 1 && parts[1].length > 1;
            default:
                return name.trim().length > 2;
        }
    }, [name, carrierId]);

    const keyboardLayout = language == Language.DE ? NameKeyboardLayoutDE : NameKeyboardLayoutEN;

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem className={layout.paddingMedium}>
                    <div className={typography.title}>
                        {t("RecipientName")}
                    </div>
                    <div className={typography.description}>
                        {t("RecipientNameDescription")}
                    </div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={layout.paddingMedium}>
                <div className={layout.horizontal}>
                    <div className={styles.field}>
                        <div className={styles.label}>
                            {t("Name")}
                        </div>
                        <TextInput
                            value={name}
                            error={nameWasTyped && !nameValid}
                            onChange={handleNameChanged}
                            inputRef={nameRef}
                            onFocus={handleNameFocus}
                            typeRef={setNameType}
                            disabled={confirm}
                            outline
                            backspace={false}
                        />
                    </div>
                    {!confirm && (
                        <div className={styles.submitField}>
                            <PushButton primary className={styles.submit} disabled={!nameValid} onClick={handleConfirm}>
                                {t("Confirm")}
                            </PushButton>
                            <PushButton className={styles.cancel} onClick={onCancel}>
                                {t("Cancel")}
                            </PushButton>
                        </div>
                    )}
                </div>
            </ViewContent>
            <ViewFooter className={classNames(layout.spaceBetween, layout.paddingMedium)}>
                {!confirm &&
                    <ViewFooterItem fill className={styles.keyboardContainer}>
                        {field == RecipientField.name && (
                            <Keyboard onType={nameType} layout={keyboardLayout}/>
                        )}
                    </ViewFooterItem>
                }
                {confirm && (
                    <>
                        <ViewFooterItem>
                            <div style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "1em"
                            }}>
                                <PushButton onClick={onCancel}>
                                    {t("Cancel")}
                                </PushButton>
                                <PushButton onClick={handleBack}>
                                    {t("Back")}
                                </PushButton>
                            </div>
                        </ViewFooterItem>
                        <ViewFooterItem>
                            <PushButton onClick={handleNextClick} primary disabled={!nameValid}>
                                {t("Next")}
                            </PushButton>
                        </ViewFooterItem>
                    </>
                )}

            </ViewFooter>
        </View>
    );
}

const styles = stylesheet({
    canvas: {
        width: "100%",
        height: "100%",
        border: "1px solid #666"
    },
    label: {
        paddingLeft: "1.5em"
    },
    field: {
        padding: "0.75rem"
    },
    submitField: {
        padding: "0.75rem",
        display: "flex",
        alignItems: "end"
    },
    submit: {
        height: "3em"
    },
    cancel: {
        height: "3em",
        marginLeft: "1.2em",
    },
    keyboardContainer: {
        padding: "0.25rem"
    },
});